import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import CandidateDashboardNav from "../candidate/CandidateDashboardNav";
import EmployerDashboardNav from "../employer/EmployerDashboardNav";
import { Link } from "react-router-dom";
import Breadcrumb from "./common/Breadcrumb";
import SuccessModal from "../shared/SuccessModal";
import { useAuth } from '../../AuthContext';

const SavedJobs = () => {
  const [savedJobs, setSavedJobs] = useState([]);
  const userid = localStorage.getItem("userid");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [JOBS_PER_PAGE] = useState(4);
  const [showModal, setShowModal] = useState(false); 
  const [modalConfig, setModalConfig] = useState({
    success: false,
    message: ""
  });
  const { auth } = useAuth();

  const fetchSavedJobs = useCallback(async () => {
    const url = `${process.env.REACT_APP_API_URL}job_wishlist/savedjobs/${userid}`;
  
    try {
      const response = await axios.get(url);
      const allJobs = response.data;
      setTotalJobs(allJobs.length);
      
      // Calculate pagination correctly
      const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
      const endIndex = startIndex + JOBS_PER_PAGE;
      const paginatedJobs = allJobs.slice(startIndex, endIndex);
      setSavedJobs(paginatedJobs);
    } catch (err) {
      console.error("Error fetching saved jobs:", err);
    }
  }, [userid, currentPage, JOBS_PER_PAGE]);

  useEffect(() => {
    fetchSavedJobs();
  }, [fetchSavedJobs]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const handleDeleteJob = async (job_wish_srno) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}job_wishlist/deletejob/${job_wish_srno}`
      );
  
      if (response.status === 200) {
        setSavedJobs((prevJobs) =>
          prevJobs.filter((job) => job.job_wish_srno !== job_wish_srno)
        );
        setModalConfig({
          success: true,
          message: "Removed from Saved jobs!"
        });
        setShowModal(true);  
        fetchSavedJobs(); // Refresh the list after deletion
      } else {
        console.error("Failed to delete job:", response.data.message);
        setModalConfig({
          success: false,
          message: "Failed to remove"
        });
        setShowModal(true);  
      }
    } catch (err) {
      console.error("Error deleting job from wishlist:", err);
      setModalConfig({
        success: false,
        message: "Failed to send the message. Please try again."
      });
      setShowModal(true);  
    }
  };

  const totalPages = Math.ceil(totalJobs / JOBS_PER_PAGE);

  return (
    <>
      <Breadcrumb pagename='Saved Jobs' />
      <div className="user-profile py-120">
        <div className="container">
          <div className="row">
            {auth.role === "candidate" ? (
              <CandidateDashboardNav />
            ) : auth.role === "employer" ? (
              <EmployerDashboardNav />
            ) : null}
            <div className="col-lg-9">
              <div className="user-profile-card">
                <div className="user-profile-card-header">
                  <h4 className="user-profile-card-title">Saved Jobs</h4>
                </div>
                <div className="table-responsive profile-applied-job">
                  <table className="table text-nowrap">
                    <thead>
                      <tr>
                        <th>Job Info</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th colSpan={2}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savedJobs.length > 0 ? (
                        savedJobs.map((job) => (
                          <tr key={job.job_id}>
                            <td>
                              <div className="profile-job-info">
                                <div className="profile-job-content">
                                  <h6>
                                    <a href="#">{job.job_name}</a>
                                  </h6>
                                  <ul className="profile-job-list">
                                    <li>
                                      <i className="far fa-briefcase"></i>{" "}
                                      {job.company_name}
                                    </li>
                                    <li>
                                      <i className="far fa-location-dot "></i>{" "}
                                      {job.city_name}, {job.state_name}, {job.country_name}
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </td>
                            <td>{formatDate(job.post_date)}</td>
                            <td>
                              <span
                                className={`badge ${job.job_status === 1
                                    ? "badge-success"
                                    : "badge-danger"
                                  }`}
                              >
                                {job.job_status === 1 ? "Active" : "Expired"}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex">
                                <Link to={`/job-details/${job.job_id}`} className="theme-btn me-2">
                                    View details
                                </Link>
                                <button
                                  onClick={() =>
                                    handleDeleteJob(job.job_wish_srno)
                                  }
                                  className="btn btn-outline-danger btn-sm px-3"
                                >
                                  <i className="far fa-trash-can"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="text-center" colSpan="4">No saved jobs available.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {totalJobs > 0 && (
                    <div className="pagination-area">
                      <div aria-label="Page navigation example">
                        <ul className="pagination">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              <span aria-hidden="true">
                                <i className="far fa-angle-double-left"/>
                              </span>
                            </button>
                          </li>
                          {[...Array(totalPages)].map((_, index) => (
                            <li
                              key={index + 1}
                              className={`page-item ${
                                currentPage === index + 1 ? "active" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(index + 1)}
                              >
                                {index + 1}
                              </button>
                            </li>
                          ))}
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              <span aria-hidden="true">
                                <i className="far fa-angle-double-right"/>
                              </span>
                            </button>
                          </li>
                        </ul>
                      </div>
                      <div className="pagination-showing">
                        <p>
                          Showing{" "}
                          {savedJobs.length > 0
                            ? (currentPage - 1) * JOBS_PER_PAGE + 1
                            : 0}{" "}
                          - {Math.min(currentPage * JOBS_PER_PAGE, totalJobs)} of{" "}
                          {totalJobs} Jobs
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SuccessModal 
        show={showModal}
        onClose={() => setShowModal(false)}
        success={modalConfig.success}
        message={modalConfig.message} />
    </>
  );
};

export default SavedJobs;